import React, { useState, useMemo } from 'react';
import ExcelJS from 'exceljs';
import Select from 'react-select';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import { formatPrice, LoadingGrid } from '../../../../../rbts/helpers';
import { useObtenerDiasPorEvento } from '../../cortesias/hooks/CortesiasHook';
import { useReporteVentasCortesiasTipo } from '../hooks/VentasCortesiasTipoHook';
import { VentaCortesiaTipoRow } from '../../../../service/reportes/VentasCortesiasTipo';


interface TipoSummary {
  tipo: string;
  total_venta: number;
  cantidad: number;
  puntosVenta: { nombre: string; total_venta: number; cantidad: number }[];
}

const formatFecha = (fechaStr: string) => {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Vista: React.FC = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [expandedTipos, setExpandedTipos] = useState<Set<string>>(new Set());

  const { list: listaDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);
  const idsDia = diasSeleccionados.map((d: any) => d.value as number);
  const { list, loading } = useReporteVentasCortesiasTipo(idsDia);

  const summaryByTipo = useMemo<TipoSummary[]>(() => {
    const map: Record<
      string,
      {
        tipo: string;
        total_venta: number;
        cantidad: number;
        pvMap: Record<string, { nombre: string; total_venta: number; cantidad: number }>;
      }
    > = {};

    list.forEach((row: VentaCortesiaTipoRow) => {
      const tipo = row.tipo ?? '—';
      if (!map[tipo]) {
        map[tipo] = { tipo, total_venta: 0, cantidad: 0, pvMap: {} };
      }
      map[tipo].total_venta += row.total_venta;
      map[tipo].cantidad += row.cantidad;

      const pv = row.punto_venta ?? '—';
      if (!map[tipo].pvMap[pv]) {
        map[tipo].pvMap[pv] = { nombre: pv, total_venta: 0, cantidad: 0 };
      }
      map[tipo].pvMap[pv].total_venta += row.total_venta;
      map[tipo].pvMap[pv].cantidad += row.cantidad;
    });

    return Object.values(map)
      .map(g => ({
        tipo: g.tipo,
        total_venta: g.total_venta,
        cantidad: g.cantidad,
        puntosVenta: Object.values(g.pvMap).sort((a, b) => a.nombre.localeCompare(b.nombre)),
      }))
      .sort((a, b) => a.tipo.localeCompare(b.tipo));
  }, [list]);

  const totalCantidad = summaryByTipo.reduce((s, g) => s + g.cantidad, 0);
  const totalMonto = summaryByTipo.reduce((s, g) => s + g.total_venta, 0);

  const toggleTipo = (tipo: string) => {
    setExpandedTipos(prev => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  };

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();

      // Hoja 1: Detalle
      const wsDetalle = workbook.addWorksheet('Detalle');
      wsDetalle.columns = [
        { header: 'Folio', key: 'folio_ticket', width: 14 },
        { header: 'Venta ID', key: 'venta_id', width: 10 },
        { header: 'Fecha / Hora', key: 'fecha_hora_venta', width: 20 },
        { header: 'Producto', key: 'producto', width: 30 },
        { header: 'Cantidad', key: 'cantidad', width: 10 },
        { header: 'Precio Unit.', key: 'precio_unitario', width: 14 },
        { header: 'Total Venta', key: 'total_venta', width: 14 },
        { header: 'Forma Pago', key: 'forma_pago', width: 14 },
        { header: 'Punto de Venta', key: 'punto_venta', width: 24 },
        { header: 'Transferido', key: 'transferido', width: 14 },
        { header: 'Tipo', key: 'tipo', width: 12 },
      ];
      wsDetalle.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      wsDetalle.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      wsDetalle.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      list.forEach((row: VentaCortesiaTipoRow) => {
        wsDetalle.addRow({
          folio_ticket: row.folio_ticket,
          venta_id: row.venta_id,
          fecha_hora_venta: formatFecha(row.fecha_hora_venta),
          producto: row.producto,
          cantidad: row.cantidad,
          precio_unitario: row.precio_unitario,
          total_venta: row.total_venta,
          forma_pago: row.forma_pago,
          punto_venta: row.punto_venta,
          transferido: row.transferido > 0 ? row.transferido : '',
          tipo: row.tipo,
        });
      });

      const totalDetRow = wsDetalle.addRow({
        folio_ticket: '',
        venta_id: '',
        fecha_hora_venta: '',
        producto: 'TOTAL',
        cantidad: totalCantidad,
        precio_unitario: '',
        total_venta: totalMonto,
        forma_pago: '',
        punto_venta: '',
        transferido: '',
        tipo: '',
      });
      totalDetRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      totalDetRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } };

      // Hoja 2: Resumen por Tipo
      const wsResumen = workbook.addWorksheet('Resumen por Tipo');
      wsResumen.columns = [
        { header: 'Tipo / Punto de Venta', key: 'label', width: 34 },
        { header: 'Cantidad', key: 'cantidad', width: 14 },
        { header: 'Suma de Total Venta', key: 'total_venta', width: 20 },
      ];
      wsResumen.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      wsResumen.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      wsResumen.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      summaryByTipo.forEach(grupo => {
        const tipoRow = wsResumen.addRow({
          label: grupo.tipo,
          cantidad: grupo.cantidad,
          total_venta: grupo.total_venta,
        });
        tipoRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        tipoRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } };

        grupo.puntosVenta.forEach(pv => {
          const pvRow = wsResumen.addRow({
            label: `   ${pv.nombre}`,
            cantidad: pv.cantidad,
            total_venta: pv.total_venta,
          });
          pvRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf0f1f5' } };
        });
      });

      const totalResRow = wsResumen.addRow({
        label: 'Total general',
        cantidad: totalCantidad,
        total_venta: totalMonto,
      });
      totalResRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      totalResRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_ventas_cortesias_tipo.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      {/* Filtro */}
      <div className="card mb-6 mb-xl-9">
        <div className="card-header border-0">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-800">Búsqueda</span>
          </h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-12 col-md-6 col-lg-5 col-xl-4">
              <div className="fs-7 text-gray-700">
                <div className="w-100 d-flex">
                  <label className="form-label w-auto">Días administrativos:</label>
                </div>
                <Select
                  options={listaDias}
                  value={diasSeleccionados}
                  styles={styleSelect}
                  theme={themeSelect}
                  classNamePrefix="tc-select"
                  placeholder="Seleccione uno o varios días..."
                  className="w-100"
                  isSearchable
                  isMulti
                  closeMenuOnSelect={false}
                  isClearable
                  isLoading={cargandoDias}
                  noOptionsMessage={() => <span>No se encontraron registros</span>}
                  onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body py-3">
            <LoadingGrid text="Cargando reporte..." />
          </div>
        </div>
      ) : idsDia.length === 0 ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body text-center py-10 text-muted fs-6">
            Selecciona al menos un día administrativo para ver el reporte.
          </div>
        </div>
      ) : list.length === 0 ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body text-center py-10 text-muted fs-6">
            No hay ventas con cortesías para los días seleccionados.
          </div>
        </div>
      ) : (
        <>
          {/* Resumen por Tipo */}
          <div className="card card-xxl-stretch mb-5 mb-xl-9">
            <div className="card-header border-0">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">Resumen por Tipo</span>
              </h3>
              <div className="card-toolbar">
                <button
                  className="btn btn-sm btn-light-success"
                  onClick={handleDownloadExcel}
                  disabled={loadingDownload}
                >
                  {loadingDownload ? (
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Descargando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    'Descargar Excel'
                  )}
                </button>
              </div>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>Tipo / Punto de Venta</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Suma de Total Venta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByTipo.map(grupo => (
                      <React.Fragment key={grupo.tipo}>
                        <tr
                          className="tc-report-group-header cursor-pointer"
                          onClick={() => toggleTipo(grupo.tipo)}
                        >
                          <td className="fw-bold fs-7 ps-3">
                            {expandedTipos.has(grupo.tipo) ? '▼' : '▶'} {grupo.tipo}
                          </td>
                          <td className="text-end fs-7 fw-bold">
                            {grupo.cantidad.toLocaleString('es-MX')}
                          </td>
                          <td className="text-end fs-7 fw-bold">
                            {formatPrice(grupo.total_venta, 'es-MX', 'MXN', 2, 2)}
                          </td>
                        </tr>
                        {expandedTipos.has(grupo.tipo) &&
                          grupo.puntosVenta.map(pv => (
                            <tr key={pv.nombre} className="tc-report-detail">
                              <td className="fs-7 ps-8">{pv.nombre}</td>
                              <td className="text-end fs-7">
                                {pv.cantidad.toLocaleString('es-MX')}
                              </td>
                              <td className="text-end fs-7">
                                {formatPrice(pv.total_venta, 'es-MX', 'MXN', 2, 2)}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tc-report-total-row">
                      <td className="fs-7 ps-3 fw-bolder">Total general</td>
                      <td className="text-end fs-7 fw-bolder">
                        {totalCantidad.toLocaleString('es-MX')}
                      </td>
                      <td className="text-end fs-7 fw-bolder">
                        {formatPrice(totalMonto, 'es-MX', 'MXN', 2, 2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Detalle de Ventas */}
          <div className="card card-xxl-stretch mb-5 mb-xl-12">
            <div className="card-header border-0">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">Detalle de Ventas con Cortesías</span>
                <span className="text-muted mt-1 fw-semibold fs-7">
                  {list.length} registro{list.length !== 1 ? 's' : ''}
                </span>
              </h3>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>Folio</th>
                      <th>Venta ID</th>
                      <th>Fecha / Hora</th>
                      <th>Producto</th>
                      <th className="text-center">Cant.</th>
                      <th className="text-end">Precio Unit.</th>
                      <th className="text-end">Total Venta</th>
                      <th className="text-center">Forma Pago</th>
                      <th>Punto de Venta</th>
                      <th className="text-end">Transferido</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row: VentaCortesiaTipoRow, idx: number) => (
                      <tr key={`${row.venta_id}-${idx}`} className="align-middle">
                        <td className="fs-7 text-muted">{row.folio_ticket}</td>
                        <td className="fs-7 text-muted">{row.venta_id}</td>
                        <td className="fs-7">{formatFecha(row.fecha_hora_venta)}</td>
                        <td className="fs-7 fw-semibold">{row.producto ?? '—'}</td>
                        <td className="text-center fs-7">{row.cantidad.toLocaleString('es-MX')}</td>
                        <td className="text-end fs-7">
                          {formatPrice(row.precio_unitario, 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td className="text-end fs-7 fw-bold" style={{ color: 'var(--tc-primary)' }}>
                          {formatPrice(row.total_venta, 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td className="text-center fs-7">
                          <span
                            className={`badge ${
                              row.forma_pago === 'Efectivo'
                                ? 'badge-light-warning'
                                : 'badge-light-primary'
                            }`}
                          >
                            {row.forma_pago}
                          </span>
                        </td>
                        <td className="fs-7">{row.punto_venta ?? '—'}</td>
                        <td className="text-end fs-7">
                          {row.transferido > 0
                            ? formatPrice(row.transferido, 'es-MX', 'MXN', 2, 2)
                            : '—'}
                        </td>
                        <td className="fs-7">
                          <span className="badge badge-light-primary">{row.tipo ?? '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tc-report-subtotal">
                      <td colSpan={4} className="fs-7 fw-bolder ps-3 tc-report-subtotal-label">
                        Totales
                      </td>
                      <td className="text-center fs-7">
                        {totalCantidad.toLocaleString('es-MX')}
                      </td>
                      <td />
                      <td className="text-end fs-7 fw-bolder tc-amount-positive">
                        {formatPrice(totalMonto, 'es-MX', 'MXN', 2, 2)}
                      </td>
                      <td colSpan={4} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { Vista };
